import {createSelector} from "@reduxjs/toolkit";

import {buildEnergyAppreciationText, isHeatingOlderThan30Years} from "@reva/shared/appreciations";
import {buildEnergyOutputRows, type EnergyOutputGroups} from "@reva/shared/energy-output";
import {hasEnergyCertificateData} from "@reva/shared/swot";

import {selectActiveValueForSingleParameter} from "./objectParametersSelectors";
import {selectAllEnergyParams, selectAllIdpEnergyCertParams, selectAllParams} from "./objectParametersSlice";
import {selectEnergyValueSpec} from "./specSlice";
import {selectEnergyValueData} from "./valueIndicationSlice";

/**
 * Ausgabeparameter der Energiekennwert-Indikation (#19381): der `energy_value_details`-Block der Wertantwort,
 * über die allgemeinen Ausgaben der Wertindikation gelegt — wie bei den anderen Teilindikationen auch.
 * Null, solange keine gelaufen ist.
 */
export const selectEnergyIndicationData = createSelector(
    [selectEnergyValueData],
    (energyValueData): Record<string, unknown> | null => energyValueData?.output ?? null,
);

export const selectEnergyRows = createSelector(
    [selectEnergyIndicationData, selectEnergyValueSpec],
    (energyData, energySpec): EnergyOutputGroups => {
        if (!energyData || !energySpec) return {};
        return buildEnergyOutputRows(energySpec.outputParameters, energyData);
    },
);

/** @return {boolean} true, if the heating system is 30 years or older as of the Wertermittlungsstichtag (#18998) */
export const selectHeatingOlderThan30Years = createSelector(
    [
        (state) =>
            selectActiveValueForSingleParameter(state, "heating_modernization_year", "INSPECTION_DERIVED")
                ?.activeParameterValue,
        (state) => selectActiveValueForSingleParameter(state, "valuation_date", "AVM")?.activeParameterValue,
    ],
    (heatingModernizationYear, valuationDate) => isHeatingOlderThan30Years(heatingModernizationYear, valuationDate),
);

/** @return {string|null} the current default text for the energy appreciation (#18998) */
export const selectEnergyAppreciationText = createSelector(
    [
        selectAllEnergyParams,
        selectAllIdpEnergyCertParams,
        selectAllParams,
        selectEnergyIndicationData,
        selectHeatingOlderThan30Years,
    ],
    (energyParams, idpEnergyParams, avmParams, energyIndicationData, heatingOlderThan30Years) =>
        buildEnergyAppreciationText({
            hasCertificate: hasEnergyCertificateData(
                energyParams as Record<string, unknown> | null,
                idpEnergyParams as Record<string, unknown> | null,
                avmParams as Record<string, unknown> | null,
            ),
            energyEfficiency: energyIndicationData?.energy_efficiency as string | null | undefined,
            energyType: energyIndicationData?.energy_type as number | null | undefined,
            heatingOlderThan30Years,
        }),
);
